import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, Navigate, useLocation, useSearchParams } from 'react-router-dom';
import Button from '@/components/base/Button';
import { useAuth } from '@/context/AuthContext';
import { useBrand } from '@/context/BrandContext';
import { publicApi } from '@/utils/api';
import CreatorAvatars from '../join/CreatorAvatars';
import { hasPhotos, type WorkingCreators } from '../join/workingCreators';
import CreatorFlow from '../join/CreatorFlow';
import PasswordInput from '@/components/base/PasswordInput';
import AppDownloadButtons from '@/components/feature/AppDownloadButtons';
import { JourneyCard, RotatingWords, type JourneyStage } from '@/components/motion/Motion';
import { greeting, stagger, useCountUp, useSpotlight } from '@/components/motion/motionHelpers';

type Audience = 'agency' | 'creator';

/**
 * One sign-in page for both sides: Agency (staff, into the dashboard) and Creator (sign up or sign
 * in, then join campaigns; never into the dashboard). `?as=creator` opens the Creator tab.
 */
export default function Login() {
  const { status } = useAuth();
  const { profile } = useBrand();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const audience: Audience = searchParams.get('as') === 'creator' ? 'creator' : 'agency';

  // Back to the page that sent the user here, or the dashboard.
  const from = (location.state as { from?: string } | null)?.from ?? '/dashboard';
  if (status === 'signedIn' && audience === 'agency') return <Navigate to={from} replace />;

  const choose = (next: Audience) => {
    const params = new URLSearchParams(searchParams);
    if (next === 'creator') params.set('as', 'creator');
    else params.delete('as');
    // Keep where staff were heading (location.state) when they switch back.
    setSearchParams(params, { replace: true, state: location.state });
  };

  return (
    <div className="twiva-brand flex min-h-screen flex-col bg-background-100 lg:flex-row">
      <BrandPanel audience={audience} />

      {/* On phones the form is a sheet pulled up over the bottom of the burgundy hero. */}
      <main className="relative z-10 -mt-8 flex flex-1 flex-col rounded-t-[28px] bg-background-100 px-4 pb-8 pt-7 shadow-[0_-10px_30px_rgba(34,33,35,0.12)] sm:px-8 lg:mt-0 lg:rounded-none lg:py-12 lg:shadow-none">
        <div className="flex flex-1 items-start justify-center lg:items-center">
          <div className={`w-full animate-fade-rise ${audience === 'creator' ? 'max-w-xl' : 'max-w-md'}`}>
            <div key={audience} className="join-cascade">
              <span className="page-rule" aria-hidden="true" />
              <h1 className="font-heading text-2xl font-semibold text-foreground-950 sm:text-3xl">
                {audience === 'agency' ? `Sign in to ${profile.name}` : 'Join campaigns on Twiva'}
              </h1>
              <p className="mt-1 text-sm text-foreground-600">
                {audience === 'agency'
                  ? `${greeting()}, welcome back. Use your agency staff account.`
                  : 'Create your creator account or sign in, then pick the campaigns you want.'}
              </p>
            </div>

            <div role="tablist" aria-label="Who is signing in" className="relative mt-6 grid grid-cols-2 gap-3">
              {/* One highlight slides to whichever side is chosen. */}
              <span
                data-audience={audience}
                className="join-tab-pill pointer-events-none absolute inset-y-0 left-0 w-[calc(50%-0.375rem)] rounded-xl border border-primary-400 bg-primary-50 shadow-sm"
                aria-hidden="true"
              />
              <AudienceTab
                active={audience === 'agency'}
                icon="ri-briefcase-4-line"
                label="I'm an agency"
                hint="Manage campaigns"
                onClick={() => choose('agency')}
              />
              <AudienceTab
                active={audience === 'creator'}
                icon="ri-user-star-line"
                label="I'm a creator"
                hint="Join campaigns"
                onClick={() => choose('creator')}
              />
            </div>

            <div className="mt-4">
              {audience === 'agency' ? (
                <AgencySignIn />
              ) : (
                <div className="rounded-xl border border-background-200 bg-background-50 p-6 shadow-sm">
                  <CreatorFlow
                    base="/join/creators"
                    intro={
                      <p className="text-sm text-foreground-700">
                        Agencies on Twiva run campaigns for brands. Create your free creator account, or sign in with the one you use in the
                        Twiva app, to see the campaigns open to you and the ones you've been invited to.
                      </p>
                    }
                  />
                </div>
              )}
            </div>
          </div>
        </div>
        {/* Creators only, phones and tablets only: the Twiva app, just above "Powered by Twiva". */}
        {audience === 'creator' ? <AppDownloadButtons className="mx-auto mt-10 w-full max-w-sm" /> : null}
        <PoweredByTwiva />
      </main>
    </div>
  );
}

interface BrandCopy {
  /** The heading's fixed start; it ends with one of `words`, changing every few seconds. */
  lead: string;
  words: string[];
  points: { icon: string; text: string }[];
  /** The example campaign card: what a campaign looks like from this side. */
  journey: { title: string; subtitle: string; stages: JourneyStage[] };
}

const BRAND_COPY: Record<Audience, BrandCopy> = {
  agency: {
    lead: 'Run creator campaigns that',
    words: ['drive sales.', 'build brands.', 'move markets.', 'get people talking.'],
    points: [
      {
        icon: 'ri-search-eye-line',
        text: 'Find and vet creators across Africa',
      },
      {
        icon: 'ri-file-list-3-line',
        text: 'Send briefs and contracts, approve content',
      },
      {
        icon: 'ri-wallet-3-line',
        text: 'Pay creators straight to their Twiva wallet',
      },
    ],
    journey: {
      title: 'Product launch campaign',
      subtitle: 'Example · 12 creators',
      stages: [
        { icon: 'ri-user-search-line', label: 'Creators shortlisted' },
        { icon: 'ri-file-text-line', label: 'Brief and contracts signed' },
        { icon: 'ri-film-line', label: 'Content approved' },
        { icon: 'ri-wallet-3-line', label: 'Creators paid to their wallets' },
      ],
    },
  },
  creator: {
    lead: 'Get paid by brands for your',
    words: ['Reels.', 'TikToks.', 'podcasts.', 'stories.', 'YouTube videos.'],
    points: [
      {
        icon: 'ri-user-star-line',
        text: 'One Twiva account for every campaign',
      },
      {
        icon: 'ri-megaphone-line',
        text: "Join open campaigns and the ones you're invited to",
      },
      { icon: 'ri-hand-coin-line', text: 'Get paid to your Twiva wallet' },
    ],
    journey: {
      title: 'Your next campaign',
      subtitle: 'Example · from invite to payout',
      stages: [
        { icon: 'ri-mail-star-line', label: 'Invited by an agency' },
        { icon: 'ri-checkbox-circle-line', label: 'Joined in one tap' },
        { icon: 'ri-film-line', label: 'Content approved' },
        { icon: 'ri-hand-coin-line', label: 'Paid to your Twiva wallet' },
      ],
    },
  },
};

/** Creator types on the brand panel, in the app's colours: shown only until real creators are on Twiva. */
const PANEL_AVATARS = [
  { icon: 'ri-camera-3-line', tone: 'bg-accent-600 text-background-50' },
  { icon: 'ri-video-on-line', tone: 'bg-background-50 text-primary-700' },
  { icon: 'ri-mic-line', tone: 'bg-primary-900 text-background-50' },
  { icon: 'ri-quill-pen-line', tone: 'bg-primary-50 text-primary-700' },
  { icon: 'ri-briefcase-4-line', tone: 'bg-accent-400 text-background-50' },
];

/** Twiva's burgundy panel: the wordmark, what this side of Twiva is for, and its creators. */
/**
 * The hero's selling points on phones: one at a time, moving on every few seconds, with dots to
 * see and pick which one is showing. Swiping still works, and touching the row pauses the
 * auto-advance for a while. No auto-advance for reduced motion; nothing moves when the row fits
 * (tablets and desktop show every point).
 */
function useChipCarousel(count: number, restartKey: string) {
  const ref = useRef<HTMLUListElement>(null);
  const [index, setIndex] = useState(0);
  const indexRef = useRef(0);
  const pausedUntil = useRef(0);

  /** How far the row scrolls to bring item `i` to the start. */
  const offsetOf = (row: HTMLUListElement, i: number) => {
    const first = row.children[0] as HTMLElement | undefined;
    const item = row.children[i] as HTMLElement | undefined;
    return first && item ? item.offsetLeft - first.offsetLeft : 0;
  };

  const goTo = (i: number) => {
    const row = ref.current;
    if (row) row.scrollTo({ left: offsetOf(row, i), behavior: 'smooth' });
  };

  /** A dot tap or a touch: let the person read before it moves on. */
  const pause = () => {
    pausedUntil.current = Date.now() + 8000;
  };

  useEffect(() => {
    const row = ref.current;
    if (!row) return undefined;
    setIndex(0);
    indexRef.current = 0;
    row.scrollTo({ left: 0 });

    // The dot follows whichever point is nearest the start, however it got there.
    const onScroll = () => {
      let nearest = 0;
      for (let i = 1; i < count; i += 1) {
        if (Math.abs(offsetOf(row, i) - row.scrollLeft) < Math.abs(offsetOf(row, nearest) - row.scrollLeft)) nearest = i;
      }
      indexRef.current = nearest;
      setIndex(nearest);
    };
    row.addEventListener('scroll', onScroll, { passive: true });
    const events = ['pointerdown', 'touchstart', 'wheel'] as const;
    events.forEach((name) => row.addEventListener(name, pause, { passive: true }));

    const timer = window.matchMedia('(prefers-reduced-motion: reduce)').matches
      ? undefined
      : window.setInterval(() => {
          if (Date.now() < pausedUntil.current || row.scrollWidth <= row.clientWidth + 4) return;
          row.scrollTo({
            left: offsetOf(row, (indexRef.current + 1) % count),
            behavior: 'smooth',
          });
        }, 3500);

    return () => {
      window.clearInterval(timer);
      row.removeEventListener('scroll', onScroll);
      events.forEach((name) => row.removeEventListener(name, pause));
    };
  }, [count, restartKey]);

  return { ref, index, goTo, pause };
}

function BrandPanel({ audience }: { audience: Audience }) {
  const copy = BRAND_COPY[audience];
  const chips = useChipCarousel(copy.points.length, audience);
  const base = import.meta.env.BASE_URL;
  // Creators working with the agency behind this custom domain, or with agencies across Twiva.
  const [showcase, setShowcase] = useState<(WorkingCreators & { agency: string | null }) | null>(null);
  useEffect(() => {
    publicApi<WorkingCreators & { agency: string | null }>(`/join/showcase?domain=${encodeURIComponent(window.location.hostname)}`)
      .then(setShowcase)
      .catch(() => setShowcase(null));
  }, []);
  const creatorCount = useCountUp(showcase?.total ?? 0);
  const spotlight = useSpotlight();
  return (
    <aside
      ref={spotlight.ref}
      onPointerMove={spotlight.onPointerMove}
      onPointerLeave={spotlight.onPointerLeave}
      className="brand-gradient relative overflow-hidden px-5 pb-16 pt-6 text-background-50 sm:px-10 lg:flex lg:w-[44%] lg:flex-col lg:justify-between lg:py-12 xl:px-16"
    >
      {/* Slow aurora colours and a light that follows the mouse. */}
      <div className="join-aurora left-[-10%] top-[20%] h-72 w-72 bg-secondary-500/40 lg:h-96 lg:w-96" aria-hidden="true" />
      <div className="join-aurora is-second bottom-[-5%] right-[-15%] h-64 w-64 bg-primary-300/25 lg:h-80 lg:w-80" aria-hidden="true" />
      <div className="join-spotlight" aria-hidden="true" />

      {/* The brand book's building blocks, as a quiet texture: they rearrange for each side. */}
      <div
        data-audience={audience}
        className="join-shapes pointer-events-none absolute -right-10 -top-10 grid grid-cols-2 gap-2 opacity-15 lg:-right-16 lg:-top-16 lg:gap-3"
        aria-hidden="true"
      >
        <span className="h-16 w-16 rounded-xl bg-background-50 lg:h-28 lg:w-28 lg:rounded-2xl" />
        <span className="h-16 w-16 rounded-xl bg-background-50/60 lg:h-28 lg:w-28 lg:rounded-2xl" />
        <span className="h-16 w-16 rounded-xl bg-background-50/40 lg:h-28 lg:w-28 lg:rounded-2xl" />
        <span className="h-16 w-16 rounded-xl bg-background-50/80 lg:h-28 lg:w-28 lg:rounded-2xl" />
      </div>
      <div
        data-audience={audience}
        className="join-glow pointer-events-none absolute -bottom-16 -left-16 h-44 w-44 rounded-full bg-background-50/10 lg:-bottom-20 lg:-left-20 lg:h-64 lg:w-64"
        aria-hidden="true"
      />

      <img src={`${base}brand/twiva-wordmark-white.png`} alt="Twiva" className="relative h-8 w-auto self-start lg:h-10" />

      {/* Keyed by side, so switching tabs replays the cascade. */}
      <div key={audience} className="join-cascade relative mt-7 lg:mt-8">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-background-50/70">
          {audience === 'agency' ? 'For agencies' : 'For creators'}
        </p>
        <h2
          className="mt-2 max-w-md font-heading text-[28px] font-semibold leading-tight sm:text-4xl lg:mt-3"
          aria-label={`${copy.lead} ${copy.words[0]}`}
        >
          {copy.lead.split(' ').map((word, index) => (
            <span key={`${word}-${index}`} aria-hidden="true">
              <span className="join-word" style={stagger(index)}>
                {word}
              </span>{' '}
            </span>
          ))}
          <span className="join-word" style={stagger(copy.lead.split(' ').length)}>
            <RotatingWords words={copy.words} />
          </span>
        </h2>
        <ul
          ref={chips.ref}
          aria-label="What Twiva does"
          className="-mx-5 mt-5 flex snap-x snap-mandatory scroll-px-5 gap-2 overflow-x-auto px-5 pb-1 [scrollbar-width:none] sm:mx-0 sm:flex-wrap sm:px-0 lg:mt-8 lg:block lg:space-y-4 lg:overflow-visible"
        >
          {copy.points.map((point) => (
            <li
              key={point.text}
              className="flex w-full shrink-0 snap-start items-center gap-2 rounded-full bg-background-50/15 py-1.5 pl-1.5 pr-3.5 text-sm text-background-50/95 sm:w-auto lg:gap-3 lg:rounded-none lg:bg-transparent lg:p-0 lg:text-base lg:text-background-50/90"
            >
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-background-50/20 lg:h-9 lg:w-9 lg:rounded-lg lg:bg-background-50/15">
                <i className={point.icon} aria-hidden="true" />
              </span>
              {point.text}
            </li>
          ))}
        </ul>
        {/* Phones: which point is showing; tap a dot to jump to it. */}
        <div className="mt-3 flex items-center gap-1.5 sm:hidden">
          {copy.points.map((point, index) => (
            <button
              key={point.text}
              type="button"
              aria-label={`Show point ${index + 1} of ${copy.points.length}`}
              aria-current={chips.index === index}
              onClick={() => {
                chips.pause();
                chips.goTo(index);
              }}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                chips.index === index ? 'w-5 bg-background-50' : 'w-1.5 bg-background-50/40'
              }`}
            />
          ))}
        </div>
      </div>

      {/* Desktop: an example campaign ticking through its stages. */}
      <div key={`journey-${audience}`} className="relative mt-10 hidden animate-fade-rise lg:block">
        <JourneyCard {...copy.journey} />
      </div>

      {showcase && hasPhotos(showcase) ? (
        <div className="relative mt-6 flex items-center gap-3 lg:mt-10 lg:gap-4">
          <CreatorAvatars creators={showcase} ring="border-primary-700" />
          <p className="max-w-[16rem] text-xs leading-snug text-background-50/85 lg:text-sm">
            <span className="font-semibold tabular-nums text-background-50">{creatorCount}</span>{' '}
            {showcase.total === 1 ? 'creator works' : 'creators work'} with {showcase.agency ?? 'agencies on Twiva'}
          </p>
        </div>
      ) : (
        <div className="relative mt-6 flex items-center gap-3 lg:mt-10 lg:gap-4">
          <div className="flex -space-x-2.5" aria-hidden="true">
            {PANEL_AVATARS.map((avatar) => (
              <span
                key={avatar.icon}
                className={`flex h-11 w-11 items-center justify-center rounded-full border-2 border-primary-700 text-lg ${avatar.tone}`}
              >
                <i className={avatar.icon} />
              </span>
            ))}
          </div>
          <p className="max-w-[16rem] text-xs leading-snug text-background-50/85 lg:text-sm">
            Photographers, video creators, podcasters, writers and the agencies that hire them
          </p>
        </div>
      )}
    </aside>
  );
}

function PoweredByTwiva() {
  return (
    <footer className="mt-10 flex flex-col items-center gap-2 text-xs text-foreground-500">
      <span className="flex items-center gap-2">
        Powered by
        <img src={`${import.meta.env.BASE_URL}brand/twiva-wordmark.png`} alt="Twiva" className="h-4 w-auto" />
      </span>
      <span>© {new Date().getFullYear()} Twiva</span>
    </footer>
  );
}

function AudienceTab({
  active,
  icon,
  label,
  hint,
  onClick,
}: {
  active: boolean;
  icon: string;
  label: string;
  hint: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={`relative flex items-center gap-3 rounded-xl border p-3 text-left transition-[border-color,background-color,transform] duration-300 active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 ${
        active ? 'border-transparent bg-transparent' : 'border-background-300 bg-background-50 hover:border-primary-300'
      }`}
    >
      <span
        className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-lg transition-colors duration-300 ${
          active ? 'brand-cta animate-pop text-background-50' : 'bg-background-200 text-foreground-600'
        }`}
      >
        <i className={icon} aria-hidden="true" />
      </span>
      <span>
        <span className={`block text-sm font-semibold ${active ? 'text-foreground-950' : 'text-foreground-800'}`}>{label}</span>
        <span className="block text-xs text-foreground-500">{hint}</span>
      </span>
    </button>
  );
}

/** The agency staff sign-in (Laravel's CPaaS login), into the dashboard. */
function AgencySignIn() {
  const { login } = useAuth();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // Counts failed tries, so each one replays the shake.
  const [failures, setFailures] = useState(0);
  // Set by the reset password page after a new password is saved.
  const notice = (location.state as { notice?: string } | null)?.notice ?? '';

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      await login(email, password);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sign-in failed.');
      setFailures((count) => count + 1);
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass =
    'mt-1 h-10 w-full rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-50';

  return (
    <form
      key={failures}
      onSubmit={handleSubmit}
      className={`rounded-xl border border-background-200 bg-background-50 p-6 shadow-sm ${failures ? 'join-shake' : ''}`}
    >
      {notice && !error ? (
        <div
          role="status"
          className="mb-4 flex items-start gap-2 rounded-md border border-accent-200 bg-accent-50 px-3 py-2 text-sm text-accent-800"
        >
          <i className="ri-checkbox-circle-line mt-0.5" />
          {notice}
        </div>
      ) : null}
      {error ? (
        <div
          role="alert"
          className="mb-4 flex items-start gap-2 rounded-md border border-primary-200 bg-primary-50 px-3 py-2 text-sm text-primary-800"
        >
          <i className="ri-error-warning-line mt-0.5" />
          {error}
        </div>
      ) : null}

      <label className="block text-sm font-medium text-foreground-800">
        Email
        <input
          type="email"
          autoComplete="email"
          required
          autoFocus
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@agency.com"
          className={inputClass}
        />
      </label>

      <div className="mt-4 flex items-center justify-between">
        <label htmlFor="login-password" className="text-sm font-medium text-foreground-800">
          Password
        </label>
        <Link to="/forgot-password" className="text-xs font-medium text-primary-700 hover:text-primary-900">
          Forgot password?
        </Link>
      </div>
      <label className="block text-sm font-medium text-foreground-800">
        <PasswordInput
          id="login-password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className={inputClass}
        />
      </label>

      <Button type="submit" size="lg" className="join-shine group mt-6 w-full" disabled={submitting}>
        {submitting ? (
          <span className="flex items-center gap-2">
            <i className="ri-loader-4-line animate-spin" aria-hidden="true" />
            Signing in…
          </span>
        ) : (
          <span className="flex items-center gap-2">
            Sign in
            <i className="ri-arrow-right-line transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />
          </span>
        )}
      </Button>
    </form>
  );
}
